import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, Transition as _Transition, unref as _unref } from "vue"

const _hoisted_1 = { class: "ev-trip-points" }
const _hoisted_2 = {
  key: 0,
  class: "page-header"
}
const _hoisted_3 = { class: "title-block" }
const _hoisted_4 = { class: "subtitle" }
const _hoisted_5 = { class: "date-tools" }
const _hoisted_6 = { class: "presets" }
const _hoisted_7 = ["disabled", "onClick"]
const _hoisted_8 = { class: "panel map-panel" }
const _hoisted_9 = { class: "map-legend" }
const _hoisted_10 = { class: "legend-item muted" }
const _hoisted_11 = { style: {"color":"#1f2937"} }
const _hoisted_12 = { style: {"font-size":"16px","margin-left":"4px"} }
const _hoisted_13 = { class: "map-wrapper" }
const _hoisted_14 = {
  key: 0,
  class: "trip-info-card"
}
const _hoisted_15 = { class: "tic-row" }
const _hoisted_16 = { class: "tic-date" }
const _hoisted_17 = { class: "tic-row" }
const _hoisted_18 = { class: "tic-date" }
const _hoisted_19 = { class: "tic-footer" }
const _hoisted_20 = {
  key: 0,
  class: "inline-loader"
}

import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
import { Loading } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import dayjs from 'dayjs'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet.markercluster'

import CommonError from '@/components/CommonError.vue'
import {
  getEvTripPoints,
  type RawTripPoint,
  type TripPointsPagination,
  type EvStatisticsRangeMeta,
  type GetEvStatisticsParams,
} from '@/api/car-ev-data'

const PER_PAGE = 200

interface SelectedTripInfo {
  startDate: string
  endDate: string
  duration: string
  status: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EVTripPoints',
  props: {
    embedded: { type: Boolean },
    dateFrom: {},
    dateTo: {}
  },
  setup(__props: any) {

const props = __props

const loading = ref(false)
const error = ref<string | null>(null)
const points = ref<RawTripPoint[]>([])
const pagination = ref<TripPointsPagination | null>(null)
const meta = ref<EvStatisticsRangeMeta | null>(null)
const page = ref(1)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rangeModel = ref<[string, string] | null>(null) as any
const activePreset = ref<string>('all')

const mapContainer = ref<HTMLElement | null>(null)
let map: L.Map | null = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let startCluster: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let endCluster: any = null

const presets = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
  { key: 'all', label: 'All' },
] as const

const formatDateShort = (iso?: string) => (iso ? dayjs(iso).format('DD MMM YYYY') : '')

const canLoadMore = computed(() => {
  const p = pagination.value
  return !!p && p.page < p.last_page
})

const currentParams = (): GetEvStatisticsParams => {
  const p: GetEvStatisticsParams = {}
  if (props.embedded) {
    if (props.dateFrom) p.date_from = props.dateFrom
    if (props.dateTo) p.date_to = props.dateTo
  } else {
    const [from, to] = rangeModel.value || []
    if (from) p.date_from = from
    if (to) p.date_to = to
  }
  return p
}

const applyPreset = (key: string) => {
  activePreset.value = key
  const today = dayjs()
  switch (key) {
    case 'today':
      rangeModel.value = [today.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')]
      break
    case '7d':
      rangeModel.value = [today.subtract(6, 'day').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')]
      break
    case '30d':
      rangeModel.value = [today.subtract(29, 'day').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')]
      break
    case 'month':
      rangeModel.value = [today.startOf('month').format('YYYY-MM-DD'), today.endOf('month').format('YYYY-MM-DD')]
      break
    case 'year':
      rangeModel.value = [today.startOf('year').format('YYYY-MM-DD'), today.endOf('year').format('YYYY-MM-DD')]
      break
    case 'all':
    default:
      rangeModel.value = null
  }
  reload()
}

const onRangeChanged = () => { activePreset.value = 'custom' }

const load = async (reset = true) => {
  try {
    loading.value = true
    error.value = null
    if (reset) {
      points.value = []
      page.value = 1
    }
    const res = await getEvTripPoints({
      ...currentParams(),
      page: page.value,
      per_page: PER_PAGE,
    })
    const list = res?.data ?? []
    points.value = reset ? list : points.value.concat(list)
    pagination.value = res?.meta?.pagination ?? null
    meta.value = res?.meta ? { range: res.meta.range } : null
    await nextTick()
    ensureMap()
    renderPoints()
  } catch (e: unknown) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = e as any
    const msg = err?.response?.data?.message || err?.message || 'Failed to load trip points'
    error.value = msg
    ElMessage.error(msg)
  } finally {
    loading.value = false
  }
}

const reload = () => {
  const [from, to] = rangeModel.value || []
  if (from && to && dayjs(from).isAfter(dayjs(to))) {
    ElMessage.error('Start date cannot be after end date')
    return
  }
  load(true)
}

const loadMore = async () => {
  if (!canLoadMore.value) return
  page.value += 1
  await load(false)
}

/* ---- Map ---- */
const parseLatLng = (raw: unknown): { lat: number; lng: number } | null => {
  if (!raw) return null
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    const lat = Number(obj.lat ?? obj.latitude)
    const lng = Number(obj.lng ?? obj.lon ?? obj.longitude)
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng }
    return null
  }
  if (typeof raw === 'string') {
    const parts = raw.split(',')
    if (parts.length < 2) return null
    const lat = parseFloat(parts[0])
    const lng = parseFloat(parts[1])
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng }
  }
  return null
}

const ensureMap = () => {
  if (map || !mapContainer.value) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete (L.Icon.Default.prototype as any)._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  })
  map = L.map(mapContainer.value).setView([40.4093, 49.8671], 12)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
  }).addTo(map)

  map.on('click', () => {
    clearSelection()
  })

  const makeCluster = (color: string) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (L as any).markerClusterGroup({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount()
        return L.divIcon({
          className: 'custom-cluster-icon',
          html: `<div style="background:${color};color:#fff;border-radius:50%;width:40px;height:40px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)">${count}</div>`,
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        })
      },
    })

  startCluster = makeCluster('#f56c6c').addTo(map)
  endCluster = makeCluster('#2ecc71').addTo(map)
}

const makePin = (color: string, label: string, pulse = false) =>
  L.divIcon({
    className: 'custom-marker-icon',
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; position: relative;">
        ${pulse ? `<div class="pulse-ring" style="border-color: ${color};"></div>` : ''}
        <div style="background: ${color}; color: #fff; width: ${pulse ? 28 : 22}px; height: ${pulse ? 28 : 22}px; border-radius: 50%; border: ${pulse ? 3 : 2}px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,.3)${pulse ? `, 0 0 16px 4px ${color}80` : ''}; display: flex; align-items: center; justify-content: center; font-size: ${pulse ? 13 : 11}px; font-weight: 800; z-index: 2; position: relative;">${label}</div>
        <div style="width: 0; height: 0; border-left: ${pulse ? 6 : 5}px solid transparent; border-right: ${pulse ? 6 : 5}px solid transparent; border-top: ${pulse ? 10 : 8}px solid ${color}; margin-top: -2px; z-index: 1;"></div>
      </div>
    `,
    iconSize: pulse ? [34, 40] : [26, 32],
    iconAnchor: pulse ? [17, 40] : [13, 32],
  })

let highlightMarkers: L.Marker[] = []

const selectedTrip = ref<SelectedTripInfo | null>(null)

const clearSelection = () => {
  highlightMarkers.forEach(m => m.remove())
  highlightMarkers = []
  selectedTrip.value = null
}

const formatTripDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

const formatDuration = (startIso: string, endIso: string) => {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
  if (ms <= 0) return '—'
  const mins = Math.round(ms / 60000)
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

const renderPoints = () => {
  if (!map || !startCluster || !endCluster) return
  const sc = startCluster
  const ec = endCluster
  sc.clearLayers()
  ec.clearLayers()
  clearSelection()

  points.value.forEach(tp => {
    const s = parseLatLng(tp.start_location)
    const e = parseLatLng(tp.end_location)

    const tripInfo: SelectedTripInfo = {
      startDate: formatTripDate(tp.start_date),
      endDate: formatTripDate(tp.end_date),
      duration: formatDuration(tp.start_date, tp.end_date),
      status: tp.status,
    }

    if (s) {
      const mStart = L.marker([s.lat, s.lng], { icon: makePin('#f56c6c', 'A') })
      mStart.on('click', () => {
        clearSelection()
        selectedTrip.value = tripInfo
        if (map) {
          // Highlight origin (A)
          highlightMarkers.push(
            L.marker([s.lat, s.lng], {
              icon: makePin('#f56c6c', 'A', true),
              zIndexOffset: 2000,
            }).addTo(map)
          )
          if (e) {
            // Highlight partner (B)
            highlightMarkers.push(
              L.marker([e.lat, e.lng], {
                icon: makePin('#2ecc71', 'B', true),
                zIndexOffset: 2000,
              }).addTo(map)
            )
            if (!map.getBounds().contains([e.lat, e.lng])) {
              map.flyToBounds(L.latLngBounds([s, e]).pad(0.15), {
                maxZoom: 15,
                duration: 0.8,
              })
            }
          }
        }
      })
      sc.addLayer(mStart)
    }
    if (e) {
      const mEnd = L.marker([e.lat, e.lng], { icon: makePin('#2ecc71', 'B') })
      mEnd.on('click', () => {
        clearSelection()
        selectedTrip.value = tripInfo
        if (map) {
          // Highlight origin (B)
          highlightMarkers.push(
            L.marker([e.lat, e.lng], {
              icon: makePin('#2ecc71', 'B', true),
              zIndexOffset: 2000,
            }).addTo(map)
          )
          if (s) {
            // Highlight partner (A)
            highlightMarkers.push(
              L.marker([s.lat, s.lng], {
                icon: makePin('#f56c6c', 'A', true),
                zIndexOffset: 2000,
              }).addTo(map)
            )
            if (!map.getBounds().contains([s.lat, s.lng])) {
              map.flyToBounds(L.latLngBounds([s, e]).pad(0.15), {
                maxZoom: 15,
                duration: 0.8,
              })
            }
          }
        }
      })
      ec.addLayer(mEnd)
    }
  })

  if (sc.getLayers().length > 0 || ec.getLayers().length > 0) {
    try {
      const group = L.featureGroup([sc, ec])
      map.fitBounds(group.getBounds().pad(0.15))
    } catch (_) {
      // ignore invalid bounds
    }
  }
  setTimeout(() => map?.invalidateSize(), 100)
}

watch(
  () => [props.dateFrom, props.dateTo],
  () => {
    if (props.embedded) {
      load(true)
    }
  },
  { immediate: true }
)

onMounted(() => {
  nextTick(() => {
    ensureMap()
    if (!props.embedded) {
      load(true)
    }
  })
})

onUnmounted(() => {
  if (map) {
    map.remove()
    map = null
  }
})

return (_ctx: any,_cache: any) => {
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_icon = _resolveComponent("el-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!_ctx.embedded)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[4] || (_cache[4] = _createElementVNode("h2", null, "Trip points", -1)),
            _createElementVNode("p", _hoisted_4, [
              _cache[3] || (_cache[3] = _createTextVNode(" Start / end geopoints of finished EV trips ")),
              (meta.value?.range?.date_from)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[1] || (_cache[1] = _createTextVNode(" · ")),
                    _createElementVNode("b", null, _toDisplayString(formatDateShort(meta.value.range.date_from)), 1),
                    _cache[2] || (_cache[2] = _createTextVNode(" — ")),
                    _createElementVNode("b", null, _toDisplayString(formatDateShort(meta.value.range.date_to)), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(presets, (p) => {
                return _createElementVNode("button", {
                  key: p.key,
                  class: _normalizeClass(["preset-btn", { active: activePreset.value === p.key }]),
                  disabled: loading.value,
                  onClick: ($event: any) => (applyPreset(p.key))
                }, _toDisplayString(p.label), 11, _hoisted_7)
              }), 64))
            ]),
            _createVNode(_component_el_date_picker, {
              modelValue: rangeModel.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((rangeModel).value = $event)),
              type: "daterange",
              "unlink-panels": "",
              "range-separator": "→",
              "start-placeholder": "From",
              "end-placeholder": "To",
              "value-format": "YYYY-MM-DD",
              format: "YYYY-MM-DD",
              size: "small",
              style: {"width":"260px"},
              onChange: onRangeChanged
            }, null, 8, ["modelValue"]),
            _createVNode(_component_el_button, {
              type: "primary",
              size: "small",
              loading: loading.value,
              onClick: reload
            }, {
              default: _withCtx(() => _cache[5] || (_cache[5] = [
                _createTextVNode(" Apply ")
              ])),
              _: 1,
              __: [5]
            }, 8, ["loading"])
          ])
        ]))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createBlock(CommonError, {
          key: 1,
          errorMessage: error.value,
          onRetryPress: reload
        }, null, 8, ["errorMessage"]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_8, [
      _createElementVNode("div", _hoisted_9, [
        _cache[7] || (_cache[7] = _createElementVNode("span", { class: "legend-item" }, [
          _createElementVNode("span", { class: "legend-color-box red" }),
          _createTextVNode(" Start point ")
        ], -1)),
        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "legend-item" }, [
          _createElementVNode("span", { class: "legend-color-box green" }),
          _createTextVNode(" End point ")
        ], -1)),
        _createElementVNode("span", _hoisted_10, [
          _cache[6] || (_cache[6] = _createTextVNode(" Trips in this period: ")),
          _createElementVNode("b", _hoisted_11, _toDisplayString(pagination.value?.total || 0), 1),
          _createElementVNode("span", _hoisted_12, " (loaded: " + _toDisplayString(points.value.length) + ") ", 1)
        ]),
        _createVNode(_component_el_button, {
          size: "small",
          disabled: !canLoadMore.value || loading.value,
          onClick: loadMore
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(canLoadMore.value ? 'Load more' : 'All loaded'), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("div", {
          ref_key: "mapContainer",
          ref: mapContainer,
          class: "trip-map"
        }, null, 512),
        _createVNode(_Transition, { name: "fade-card" }, {
          default: _withCtx(() => [
            (selectedTrip.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  _createElementVNode("div", _hoisted_15, [
                    _cache[9] || (_cache[9] = _createElementVNode("span", { class: "tic-dot tic-dot-a" }, "A", -1)),
                    _createElementVNode("span", _hoisted_16, _toDisplayString(selectedTrip.value.startDate), 1)
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _cache[10] || (_cache[10] = _createElementVNode("span", { class: "tic-dot tic-dot-b" }, "B", -1)),
                    _createElementVNode("span", _hoisted_18, _toDisplayString(selectedTrip.value.endDate), 1)
                  ]),
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("b", null, _toDisplayString(selectedTrip.value.duration), 1),
                    _createTextVNode(" · " + _toDisplayString(selectedTrip.value.status), 1)
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _createVNode(_component_el_icon, { class: "is-loading" }, {
              default: _withCtx(() => [
                _createVNode(_unref(Loading))
              ]),
              _: 1
            }),
            _cache[11] || (_cache[11] = _createElementVNode("span", null, "Loading trip points…", -1))
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})